import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { useCareCoordinators, type CareCoordinator } from './shared/useCareCoordinators'

export type OrderTypeLabel = 'Labcorp' | 'Radiology' | 'Quest'

type Props = {
  opened: boolean
  patientName: string
  orderType: OrderTypeLabel
  defaultNote: string
  saving: boolean
  onCancel: () => void
  onSaveDraftOnly: () => void | Promise<void>
  onSaveAndSend: (input: {
    coordinator: CareCoordinator
    subject: string
    message: string
  }) => void | Promise<void>
}

function buildDefaultSubject(patientName: string, orderType: OrderTypeLabel): string {
  const name = patientName.trim() || 'patient'
  return `Facility needed — ${name} ${orderType} order`
}

export function SaveDraftCoordinatorModal({
  opened,
  patientName,
  orderType,
  defaultNote,
  saving,
  onCancel,
  onSaveDraftOnly,
  onSaveAndSend,
}: Props) {
  const { coordinators, loading, error } = useCareCoordinators(opened)
  const [coordinatorId, setCoordinatorId] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (opened) {
      setCoordinatorId(null)
      setSubject(buildDefaultSubject(patientName, orderType))
      setMessage(defaultNote)
    }
  }, [opened, patientName, orderType, defaultNote])

  const selectedCoordinator = coordinators.find((c) => c.id === coordinatorId)
  const canSendMessage =
    !!selectedCoordinator && subject.trim().length > 0 && message.trim().length > 0

  const handleSend = async () => {
    if (!selectedCoordinator) return
    await onSaveAndSend({
      coordinator: selectedCoordinator,
      subject: subject.trim(),
      message: message.trim(),
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title="Save Draft"
      size="lg"
      closeOnClickOutside={!saving}
      closeOnEscape={!saving}
      withCloseButton={!saving}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          No facility is selected on this order. Save it as a draft, and optionally send a message
          to a care coordinator so they can assign the facility.
        </Text>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {error}
          </Alert>
        )}

        <Select
          label="Care coordinator"
          placeholder={loading ? 'Loading…' : 'Select a care coordinator'}
          data={coordinators.map((c) => ({ value: c.id, label: c.name }))}
          value={coordinatorId}
          onChange={setCoordinatorId}
          disabled={loading || saving || coordinators.length === 0}
          searchable
          nothingFoundMessage="No care coordinators found"
          description={
            !loading && coordinators.length === 0
              ? 'No coordinators are available. You can still save the draft.'
              : undefined
          }
        />

        <TextInput
          label="Subject"
          value={subject}
          onChange={(event) => setSubject(event.currentTarget.value)}
          disabled={saving}
        />

        <Textarea
          label="Message to care coordinator"
          value={message}
          onChange={(event) => setMessage(event.currentTarget.value)}
          minRows={4}
          autosize
          disabled={saving}
          description="Prefilled from the note you entered on the facility step. Edit as needed."
        />

        <Group justify="space-between" mt="sm">
          <Button variant="subtle" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Group>
            <Button variant="default" onClick={onSaveDraftOnly} loading={saving}>
              Save draft only
            </Button>
            <Button onClick={handleSend} disabled={!canSendMessage} loading={saving}>
              Save draft & send message
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  )
}
